import { getStartParam, getTelegramUserId } from './tg.js';

/**
 * Контекст запуска, переживающий холодный перезапуск приложения — work-038.
 *
 * Когда пропуск Telegram (`initData`) протухает, единственное лечение —
 * открыть мини-апп заново: Telegram выдаёт пропуск один раз при запуске, а SDK
 * держит launch params в `sessionStorage` (`tapps/launchParams`), поэтому reload
 * страницы возвращает ТОТ ЖЕ старый пропуск. Перезапуск уничтожает всё, что
 * человек успел набрать, и адрес, по которому он пришёл. Здесь мы это сохраняем.
 *
 * Хранилище — `localStorage`, а не `sessionStorage`: последний умирает вместе
 * с WebView, а чиним мы ровно холодный перезапуск.
 *
 * localStorage может быть недоступен (private mode, отключённое хранилище) —
 * читаем/пишем в try/catch, деградируем к «контекста нет» / «не сохранилось»,
 * а не к падению (CANON G3, зеркалим `lib/sentQuestions.ts`).
 */

export const LAUNCH_CONTEXT_KEY = 'veylme.launchContext';

/** TTL всей записи — 24 ч. Старше — считаем, что записи нет, ключ стираем. */
const TTL_MS = 86_400_000;

/**
 * Отдельный TTL на КАЖДЫЙ черновик — тоже 24 ч, но считается от момента, когда
 * написали именно его.
 *
 * Зачем отдельный: `savedAt` записи переставляется при любой записи (черновик
 * соседнего адресата, метка перезапуска), то есть TTL всей записи «скользит» и
 * при регулярном пользовании не наступает никогда. Без собственной отметки
 * недописанное анонимное сообщение всплывало бы в поле ввода спустя недели.
 */
const DRAFT_TTL_MS = 86_400_000;

/**
 * Окно доверия к метке перезапуска — 10 минут.
 *
 * Сохранённый `startParam` подставляется ТОЛЬКО внутри этого окна после
 * нашего собственного перезапуска. Без привязки к метке любой штатный вход
 * из меню бота в течение суток провалил бы человека на чужой ASK-экран.
 */
const RESTART_WINDOW_MS = 600_000;

/** Потолок черновиков: больше пяти адресатов одновременно не бывает. */
const MAX_DRAFTS = 5;

/** Потолок длины черновика — как раз выше лимита текста вопроса/ответа. */
const MAX_DRAFT_TEXT = 1000;

/**
 * Адресат черновика.
 *
 * Черновик ОБЯЗАН нести адресата, а не только вид экрана: иначе текст,
 * написанный получателю A, подставился бы в поле при открытии ссылки
 * получателя B, и человек отправил бы личное сообщение не тому. Для продукта
 * про анонимную переписку это худший класс ошибки.
 */
export type DraftTarget =
  | { screen: 'ASK'; token: string }
  | { screen: 'ASK_BY_CODE'; code: string }
  | { screen: 'ANSWER'; questionId: string };

/**
 * Черновик = адресат + набранный текст. `at` (момент записи, unix ms)
 * проставляется самим `saveDraft` — вызывающему его передавать не нужно.
 */
export type Draft = DraftTarget & { text: string; at?: number };

/** Метка «перезапуск инициировали мы сами» + пропуск, который был ДО него. */
export interface RestartMark {
  /** `auth_date` до перезапуска. Совпал после — Telegram отдал тот же пропуск. */
  authDate?: number;
  /**
   * СНИМОК `start_param` того запуска, который перезапускали.
   *
   * Именно снимок, а не общее поле `startParam` записи: метка доказывает «этот
   * перезапуск наш», но НИЧЕГО не говорит о том, что лежащий в записи адрес
   * принадлежит перезапущенному запуску. Без снимка выходило так: человек утром
   * пришёл по чужой ask-ссылке (адрес сохранился), вечером открыл приложение из
   * меню бота, упёрся в 401 и нажал «открыть заново» — и попадал на форму
   * вопроса ЧУЖОГО ящика восьмичасовой давности вместо своего инбокса, теряя
   * при этом метрику `app_opened` и онбординг кода.
   */
  startParam?: string;
  /** Момент постановки метки, unix ms. */
  at: number;
}

export interface LaunchContext {
  /**
   * Версия формата. 2 — добавлены `uid` (разделение аккаунтов) и `at` у каждого
   * черновика (собственный срок годности). Записи версии 1 не читаются и
   * стираются: черновики эфемерны, конвертировать их незачем.
   */
  v: 2;
  /**
   * Telegram-id владельца записи. Разные аккаунты на одном устройстве делят
   * общий `localStorage`; без этого поля недописанное анонимное сообщение
   * одного аккаунта подставилось бы в поле ввода другому.
   */
  uid?: string;
  /** Сырой `tgWebAppStartParam` последнего «живого» запуска. */
  startParam?: string;
  drafts?: Draft[];
  restart?: RestartMark;
  /** Момент последней записи, unix ms (на нём стоит TTL всей записи). */
  savedAt: number;
}

/* ------------------------------------------------------------------ */
/* Валидация формы (JSON.parse доверять нельзя — в ключ мог попасть кто угодно) */
/* ------------------------------------------------------------------ */

function isDraft(value: unknown): value is Draft {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.text !== 'string') return false;
  if (v.at !== undefined && (typeof v.at !== 'number' || !Number.isFinite(v.at))) return false;
  switch (v.screen) {
    case 'ASK':
      return typeof v.token === 'string' && v.token.length > 0;
    case 'ASK_BY_CODE':
      return typeof v.code === 'string' && v.code.length > 0;
    case 'ANSWER':
      return typeof v.questionId === 'string' && v.questionId.length > 0;
    default:
      return false;
  }
}

function isRestartMark(value: unknown): value is RestartMark {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.at !== 'number' || !Number.isFinite(v.at)) return false;
  if (
    v.authDate !== undefined &&
    (typeof v.authDate !== 'number' || !Number.isFinite(v.authDate))
  ) {
    return false;
  }
  if (v.startParam !== undefined && typeof v.startParam !== 'string') return false;
  return true;
}

function isLaunchContext(value: unknown): value is LaunchContext {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.v !== 2) return false;
  if (typeof v.savedAt !== 'number' || !Number.isFinite(v.savedAt)) return false;
  if (v.uid !== undefined && typeof v.uid !== 'string') return false;
  if (v.startParam !== undefined && typeof v.startParam !== 'string') return false;
  if (v.drafts !== undefined && (!Array.isArray(v.drafts) || !v.drafts.every(isDraft))) {
    return false;
  }
  if (v.restart !== undefined && !isRestartMark(v.restart)) return false;
  return true;
}

/* ------------------------------------------------------------------ */
/* Чтение / запись                                                     */
/* ------------------------------------------------------------------ */

/** Стереть ключ. Молча no-op при недоступном localStorage. */
function dropKey(): void {
  try {
    localStorage.removeItem(LAUNCH_CONTEXT_KEY);
  } catch {
    // Хранилище недоступно — стирать нечего и незачем (CANON G3).
  }
}

/**
 * Прочитать контекст запуска.
 *
 * `undefined` при: пустом ключе, битом JSON, чужой/невалидной форме, `v !== 1`
 * и записи старше 24 ч. В трёх последних случаях ключ дополнительно стирается —
 * мусор не должен жить вечно.
 */
export function readLaunchContext(): LaunchContext | undefined {
  try {
    const raw = localStorage.getItem(LAUNCH_CONTEXT_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!isLaunchContext(parsed)) {
      dropKey();
      return undefined;
    }
    if (Date.now() - parsed.savedAt > TTL_MS) {
      dropKey();
      return undefined;
    }
    // Запись принадлежит ДРУГОМУ аккаунту Telegram — она не наша. Стираем:
    // в продукте про анонимные сообщения чужой недописанный текст в поле
    // ввода недопустим, а держать его «на всякий случай» незачем.
    // Оба id известны и не совпали — только тогда. Неизвестный id (вне
    // Telegram, тесты) записи не отбирает.
    const uid = getTelegramUserId();
    if (parsed.uid !== undefined && uid !== undefined && parsed.uid !== uid) {
      dropKey();
      return undefined;
    }
    return parsed;
  } catch {
    // Битый JSON или недоступное хранилище — контекста нет (CANON G3).
    return undefined;
  }
}

/** Записать контекст, освежив `savedAt`. Молча no-op при отказе хранилища. */
function writeLaunchContext(next: Omit<LaunchContext, 'v' | 'savedAt' | 'uid'>): void {
  try {
    const record: LaunchContext = {
      v: 2,
      ...next,
      uid: getTelegramUserId(),
      savedAt: Date.now(),
    };
    localStorage.setItem(LAUNCH_CONTEXT_KEY, JSON.stringify(record));
  } catch {
    // Квота исчерпана / хранилище недоступно — контекст не переживёт
    // перезапуск. Допустимый деградированный путь (CANON G3).
  }
}

/** Текущая запись без TTL-фильтра не нужна — работаем поверх `readLaunchContext`. */
function currentOrEmpty(): Omit<LaunchContext, 'v' | 'savedAt' | 'uid'> {
  const ctx = readLaunchContext();
  return {
    startParam: ctx?.startParam,
    drafts: ctx?.drafts,
    restart: ctx?.restart,
  };
}

/**
 * Запомнить `start_param` этого запуска.
 *
 * Пустое значение НЕ затирает прошлое: штатный вход из меню бота приходит без
 * `start_param`, и он не должен стирать адрес, по которому человек пришёл.
 */
export function saveStartParam(raw: string | undefined): void {
  if (raw === undefined || raw === '') return;
  writeLaunchContext({ ...currentOrEmpty(), startParam: raw });
}

/* ------------------------------------------------------------------ */
/* Черновики                                                           */
/* ------------------------------------------------------------------ */

/** Совпадение адресата — полное: вид экрана И идентификатор получателя. */
function sameTarget(a: DraftTarget | Draft, b: DraftTarget | Draft): boolean {
  if (a.screen !== b.screen) return false;
  switch (a.screen) {
    case 'ASK':
      return a.token === (b as { token: string }).token;
    case 'ASK_BY_CODE':
      return a.code === (b as { code: string }).code;
    case 'ANSWER':
      return a.questionId === (b as { questionId: string }).questionId;
    default:
      return false;
  }
}

/**
 * Сохранить черновик. Текст обрезается до `MAX_DRAFT_TEXT`, черновик того же
 * адресата перезаписывается, всего храним не больше `MAX_DRAFTS` — вытесняется
 * самый старый.
 *
 * ПУСТОЙ ТЕКСТ ЧЕРНОВИКОМ НЕ СЧИТАЕТСЯ и вместо записи СТИРАЕТ прежний.
 * Иначе каждая карточка вопроса в инбоксе писала бы пустышку сразу на
 * монтировании (композер ответа стартует с пустой строки), пять таких пустышек
 * забивали бы лимит `MAX_DRAFTS` и вытесняли единственный настоящий черновик —
 * ровно тот текст, ради спасения которого весь этот модуль и существует.
 * Поймано живой проверкой в браузере, юнит-тестами не ловилось.
 */
export function saveDraft(draft: Draft): void {
  if (draft.text.trim() === '') {
    clearDraft(draft);
    return;
  }
  const ctx = currentOrEmpty();
  const kept = (ctx.drafts ?? []).filter((d) => !sameTarget(d, draft));
  // `at` проставляем здесь, а не доверяем вызывающему: срок годности черновика
  // должен считаться от момента реальной записи.
  const trimmed: Draft = {
    ...draft,
    text: draft.text.slice(0, MAX_DRAFT_TEXT),
    at: Date.now(),
  };
  const next = [...kept, trimmed].slice(-MAX_DRAFTS);
  writeLaunchContext({ ...ctx, drafts: next });
}

/**
 * Прочитать черновик — ТОЛЬКО при полном совпадении адресата.
 *
 * Не совпало — `undefined`. Никаких «похожих» подстановок: чужой текст в поле
 * анонимного сообщения означает, что человек отправит его не тому.
 */
export function readDraft(target: DraftTarget): string | undefined {
  const ctx = readLaunchContext();
  const draft = ctx?.drafts?.find((d) => sameTarget(d, target));
  if (!draft) return undefined;
  // Собственный срок годности черновика: `savedAt` всей записи скользит от
  // любой соседней записи, поэтому на него полагаться нельзя (см. DRAFT_TTL_MS).
  if (draft.at !== undefined && Date.now() - draft.at > DRAFT_TTL_MS) return undefined;
  return draft.text;
}

/** Удалить черновик указанного адресата (после успешной отправки). */
export function clearDraft(target: DraftTarget): void {
  const ctx = currentOrEmpty();
  if (!ctx.drafts?.length) return;
  const next = ctx.drafts.filter((d) => !sameTarget(d, target));
  if (next.length === ctx.drafts.length) return;
  writeLaunchContext({ ...ctx, drafts: next });
}

/* ------------------------------------------------------------------ */
/* Метка перезапуска                                                   */
/* ------------------------------------------------------------------ */

/**
 * Поставить метку «перезапуск инициировали мы» и запомнить пропуск ДО него.
 *
 * Вызывается экраном истёкшей сессии ПЕРЕД попыткой открыть приложение заново.
 * Метка нужна двум вещам: она разрешает подставить сохранённый `startParam`
 * (см. `resolveStartParam`) и позволяет после перезапуска понять, что Telegram
 * отдал ТОТ ЖЕ старый пропуск (баг Telegram Desktop #28303).
 */
export function markRestart(authDateSec: number | undefined): void {
  const ctx = currentOrEmpty();
  writeLaunchContext({
    ...ctx,
    restart: {
      authDate: authDateSec,
      // Снимок адреса ИМЕННО ЭТОГО запуска: `resolveStartParam()` уже
      // отработал и мемоизирован к моменту, когда экран истёкшей сессии зовёт
      // `markRestart`. Пустой снимок (запуск без адреса) — это тоже ответ:
      // после перезапуска подставлять будет нечего, и это правильно.
      startParam: resolveStartParam(),
      at: Date.now(),
    },
  });
}

/**
 * Снять метку перезапуска, если он УДАЛСЯ.
 *
 * `currentAuthDateSec` — пропуск текущего запуска. Совпал с запомненным — это
 * тот самый залипший пропуск (#28303), метку ОСТАВЛЯЕМ: она нужна экрану, чтобы
 * показать честный текст «Telegram выдал тот же старый пропуск». Отличается
 * (или неизвестен с любой стороны) — перезапуск удался, метку снимаем.
 *
 * Вызов без аргумента снимает метку безусловно — для случая, когда сверку
 * `auth_date` делает сам вызывающий.
 */
export function clearRestartMark(currentAuthDateSec?: number): void {
  const ctx = currentOrEmpty();
  if (!ctx.restart) return;
  const stuck =
    ctx.restart.authDate !== undefined &&
    currentAuthDateSec !== undefined &&
    ctx.restart.authDate === currentAuthDateSec;
  if (stuck) return;
  writeLaunchContext({ ...ctx, restart: undefined });
}

/* ------------------------------------------------------------------ */
/* start_param этого запуска                                           */
/* ------------------------------------------------------------------ */

/** Мемо на время жизни модуля: за один запуск ответ обязан быть одним и тем же. */
let startParamMemo: { value: string | undefined } | undefined;

function computeStartParam(): string | undefined {
  try {
    const live = getStartParam();
    if (live !== undefined && live !== '') return live;
    const ctx = readLaunchContext();
    // Подставляем ТОЛЬКО снимок из метки перезапуска — адрес того самого
    // запуска, который перезапускали, и только внутри окна в 10 минут.
    //
    // Читать общее поле `ctx.startParam` здесь НЕЛЬЗЯ (проверено ревью, MAJOR):
    // метка доказывает «перезапуск наш», но не доказывает, что лежащий в записи
    // адрес принадлежит перезапущенному запуску — и человека выбрасывало на
    // чужой ASK-экран из прошлого визита. Условие «есть метка» защищает от
    // штатного входа из меню бота, снимок — от чужого адреса внутри перезапуска.
    if (!ctx?.restart) return undefined;
    if (Date.now() - ctx.restart.at >= RESTART_WINDOW_MS) return undefined;
    return ctx.restart.startParam;
  } catch {
    return undefined;
  }
}

/**
 * Единственный источник истины о `start_param` в этом запуске.
 *
 * Живой непустой → он; иначе сохранённый, но лишь при свежей метке
 * перезапуска; иначе `undefined`.
 *
 * Сохранённый `startParam` при восстановлении НЕ стирается: карусель бага
 * Desktop #28303 может потребовать второго круга, и на нём ссылка возврата
 * не должна выродиться в голый `?startapp`. От «залипания» защищают метка
 * перезапуска (без неё сохранённое значение вообще не читается) и TTL 24 ч.
 */
export function resolveStartParam(): string | undefined {
  if (startParamMemo) return startParamMemo.value;
  startParamMemo = { value: computeStartParam() };
  return startParamMemo.value;
}

/**
 * Запомнить `start_param` ЭТОГО запуска, если он живой и непустой.
 *
 * Обёртка существует ради `App.tsx`: там запрещено читать живое значение
 * напрямую — все точки маршрутизации обязаны идти через `resolveStartParam()`,
 * иначе после перезапуска человек попадёт не на тот экран, а рекламная метка
 * не восстановится. Живое значение нужно ровно в одном месте — чтобы его
 * сохранить, и это место здесь.
 */
export function captureLiveStartParam(): void {
  try {
    saveStartParam(getStartParam());
  } catch {
    // Контекст не сохранился — допустимая деградация (CANON G3).
  }
}

/**
 * Доступно ли хранилище для записи.
 *
 * Нужно экрану истёкшей сессии: при недоступном `localStorage` мы физически не
 * сохранили черновик и не сможем распознать залипший пропуск — значит обещать
 * «начатый текст мы сохранили» нельзя, это было бы ровно то враньё, ради
 * устранения которого затевался work-038.
 *
 * Проверяем пробной записью, а не наличием объекта: `localStorage` может
 * существовать и бросать на `setItem` (переполненная квота, отключённое
 * хранилище в настройках).
 */
export function isStorageAvailable(): boolean {
  try {
    const probe = `${LAUNCH_CONTEXT_KEY}.probe`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/** Стереть весь контекст запуска. */
export function clearLaunchContext(): void {
  dropKey();
}

/** Сбросить мемо `resolveStartParam` — только для тестов. */
export function __resetStartParamMemoForTests(): void {
  startParamMemo = undefined;
}
